import { ChevronRight } from 'lucide-react'
import * as React from 'react'

import { cn } from '@/lib/utils'

export interface BreadcrumbItem {
  /**
   * Display text for the breadcrumb item
   */
  label: string
  /**
   * Optional click handler for navigation
   */
  onClick?: () => void
  /**
   * Optional href for anchor navigation
   */
  href?: string
}

export interface BreadcrumbProps extends React.HTMLAttributes<HTMLElement> {
  /**
   * Array of breadcrumb items to display
   */
  items: BreadcrumbItem[]
  /**
   * Optional icon to display at the start
   */
  icon?: React.ReactNode
  /**
   * Separator between items (defaults to ChevronRight)
   */
  separator?: React.ReactNode
}

const Breadcrumb = React.forwardRef<HTMLElement, BreadcrumbProps>(
  ({ items, icon, separator, className, ...props }, ref) => {
    const defaultSeparator = <ChevronRight className="h-4 w-4 text-[#737373]" />

    return (
      <nav
        ref={ref}
        {...props}
        aria-label="Breadcrumb"
        className={cn('flex w-full items-center font-[Geist]', className)}
      >
        <ol className="flex items-center gap-2" role="list">
          {/* First item with optional icon */}
          {items[0] && (
            <li className="flex items-center gap-2">
              {icon && (
                <div aria-hidden="true" className="text-[#737373]">
                  {icon}
                </div>
              )}
              {items[0].href ? (
                <a
                  className={cn(
                    'font-geist text-sm font-normal leading-5 text-[#737373] hover:cursor-default',
                    items[0].onClick &&
                      'hover:cursor-pointer hover:text-[#262626] hover:underline'
                  )}
                  href={items[0].href}
                  onClick={(e) => {
                    if (items[0].onClick) {
                      e.preventDefault()
                      items[0].onClick()
                    }
                  }}
                >
                  {items[0].label}
                </a>
              ) : (
                <span
                  className={cn(
                    'font-geist text-sm font-normal leading-5 text-[#737373] hover:cursor-default',
                    items[0].onClick &&
                      'hover:cursor-pointer hover:text-[#262626] hover:underline'
                  )}
                  onClick={() => items[0].onClick?.()}
                  onKeyDown={(e) => {
                    if (
                      items[0].onClick &&
                      (e.key === 'Enter' || e.key === ' ')
                    ) {
                      e.preventDefault()
                      items[0].onClick()
                    }
                  }}
                  role={items[0].onClick ? 'button' : undefined}
                  tabIndex={items[0].onClick ? 0 : undefined}
                >
                  {items[0].label}
                </span>
              )}
            </li>
          )}

          {/* Remaining items with separator */}
          {items.slice(1).map((item, index) => {
            const isLast = index === items.slice(1).length - 1

            return (
              <li className="flex items-center" key={`${item.label}-${index}`}>
                <div aria-hidden="true" className="mx-1">
                  {separator || defaultSeparator}
                </div>
                {item.href ? (
                  <a
                    aria-current={isLast ? 'page' : undefined}
                    className={cn(
                      'font-geist text-sm font-normal leading-5 text-[#737373] hover:cursor-default',
                      item.onClick &&
                        'hover:cursor-pointer hover:text-[#262626] hover:underline',
                      isLast && 'text-[#262626]'
                    )}
                    href={item.href}
                    onClick={(e) => {
                      if (item.onClick) {
                        e.preventDefault()
                        item.onClick()
                      }
                    }}
                  >
                    {item.label}
                  </a>
                ) : (
                  <span
                    aria-current={isLast ? 'page' : undefined}
                    className={cn(
                      'font-geist text-sm font-normal leading-5 text-[#737373] hover:cursor-default',
                      item.onClick &&
                        'hover:cursor-pointer hover:text-[#262626] hover:underline',
                      isLast && 'text-[#262626]'
                    )}
                    onClick={() => item.onClick?.()}
                    onKeyDown={(e) => {
                      if (
                        item.onClick &&
                        (e.key === 'Enter' || e.key === ' ')
                      ) {
                        e.preventDefault()
                        item.onClick()
                      }
                    }}
                    role={item.onClick ? 'button' : undefined}
                    tabIndex={item.onClick ? 0 : undefined}
                  >
                    {item.label}
                  </span>
                )}
              </li>
            )
          })}
        </ol>
      </nav>
    )
  }
)

Breadcrumb.displayName = 'Breadcrumb'

export { Breadcrumb }
